import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ReactFlow,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  Background,
  Controls,
  MiniMap,
  type Connection,
  type Node,
  type Edge,
  type NodeChange,
  type EdgeChange,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { GitBranch } from 'lucide-react'
import { EmptyState } from '@/components/ui'
import { StepFlowNode, type StepFlowNodeData } from './nodes/StepFlowNode.tsx'
import type { TemplateStep, TemplateTransition } from './types.ts'

const NODE_TYPES = {
  step: StepFlowNode,
  approval: StepFlowNode,
  condition: StepFlowNode,
}

function getNodeType(stepType: string): 'step' | 'approval' | 'condition' {
  if (stepType === 'approval') return 'approval'
  if (stepType === 'condition') return 'condition'
  return 'step'
}

function buildRfNodes(steps: TemplateStep[]): Node[] {
  return steps.map((step, index) => ({
    id: step.id,
    type: getNodeType(step.step_type),
    position: step.config?.position ?? { x: 250, y: index * 160 },
    data: { step } as Pick<StepFlowNodeData, 'step'>,
  }))
}

function buildRfEdges(transitions: TemplateTransition[]): Edge[] {
  return transitions.map((t) => ({
    id: t.id,
    source: t.from_step_id,
    target: t.to_step_id,
    label: t.label ?? (t.condition ? 'wenn …' : undefined) ?? (t.is_default ? 'sonst' : undefined),
    labelStyle: { fontSize: 11, fill: '#6b7280' },
  }))
}

interface ProcessCanvasProps {
  steps: TemplateStep[]
  transitions: TemplateTransition[]
  selectedStepId: string | null
  selectedEdgeId: string | null
  onSelectStep: (id: string) => void
  onSelectEdge: (id: string) => void
  onToggleEnabled: (id: string, enabled: boolean) => void
  onDelete: (id: string) => void
  onNodeMove: (id: string, position: { x: number; y: number }) => void
  onEdgeAdd: (sourceId: string, targetId: string) => void
  onEdgeDelete: (transitionId: string) => void
}

export function ProcessCanvas({
  steps,
  transitions,
  selectedStepId,
  selectedEdgeId,
  onSelectStep,
  onSelectEdge,
  onToggleEnabled,
  onDelete,
  onNodeMove,
  onEdgeAdd,
  onEdgeDelete,
}: ProcessCanvasProps) {
  const [nodes, setNodes] = useState<Node[]>(() => buildRfNodes(steps))
  const [edges, setEdges] = useState<Edge[]>(() => buildRfEdges(transitions))

  // Sync when steps change (add/delete/data update) while preserving current positions
  useEffect(() => {
    setNodes((prev: Node[]) => {
      const prevById = new Map<string, Node>(prev.map((n: Node) => [n.id, n]))
      return steps.map((step, index) => {
        const existing: Node | undefined = prevById.get(step.id)
        return {
          id: step.id,
          type: getNodeType(step.step_type),
          position: step.config?.position ?? existing?.position ?? { x: 250, y: index * 160 },
          data: { step } as Pick<StepFlowNodeData, 'step'>,
        }
      })
    })
  }, [steps])

  // Rebuild edges when transitions change.
  useEffect(() => {
    setEdges(buildRfEdges(transitions))
  }, [transitions])

  const handleNodesChange = useCallback(
    (changes: NodeChange[]) => {
      for (const change of changes) {
        if (change.type === 'position' && change.dragging === false && change.position) {
          onNodeMove(change.id, change.position)
        }
      }
      setNodes((nds: Node[]) => applyNodeChanges(changes, nds))
    },
    [onNodeMove],
  )

  const handleEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      setEdges((eds: Edge[]) => {
        for (const change of changes) {
          if (change.type === 'remove') {
            onEdgeDelete(change.id)
          }
        }
        return applyEdgeChanges(changes, eds)
      })
    },
    [onEdgeDelete],
  )

  const handleConnect = useCallback(
    (connection: Connection) => {
      if (connection.source && connection.target) {
        setEdges((eds: Edge[]) => addEdge(connection, eds))
        onEdgeAdd(connection.source, connection.target)
      }
    },
    [onEdgeAdd],
  )

  // Inject per-node callbacks without recreating underlying node objects
  const nodesWithCallbacks = useMemo(
    () =>
      nodes.map((n: Node) => ({
        ...n,
        data: {
          ...(n.data as object),
          isSelected: n.id === selectedStepId,
          onSelect: () => onSelectStep(n.id),
          onToggleEnabled: (enabled: boolean) => onToggleEnabled(n.id, enabled),
          onDelete: () => onDelete(n.id),
        } as StepFlowNodeData,
      })) as unknown as Node[],
    // Stable parent callbacks; recreate only when nodes or selection changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nodes, selectedStepId],
  )

  // Highlight the selected edge.
  const styledEdges = useMemo(
    () =>
      edges.map((e: Edge) => ({
        ...e,
        style:
          e.id === selectedEdgeId
            ? { stroke: '#2563eb', strokeWidth: 2.5 }
            : { stroke: '#6b7280', strokeWidth: 2 },
      })),
    [edges, selectedEdgeId],
  )

  if (steps.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <EmptyState
          icon={<GitBranch className="w-8 h-8" />}
          title="Noch keine Schritte"
          description="Fügen Sie links Schritte hinzu, um den Prozessablauf zu gestalten."
        />
      </div>
    )
  }

  return (
    <ReactFlow
      nodes={nodesWithCallbacks as unknown as Node[]}
      edges={styledEdges}
      nodeTypes={NODE_TYPES}
      onNodesChange={handleNodesChange}
      onEdgesChange={handleEdgesChange}
      onConnect={handleConnect}
      onEdgeClick={(_, edge) => onSelectEdge(edge.id)}
      deleteKeyCode="Delete"
      defaultEdgeOptions={{ type: 'smoothstep', style: { stroke: '#6b7280', strokeWidth: 2 } }}
      fitView
      fitViewOptions={{ padding: 0.25 }}
      minZoom={0.3}
      maxZoom={2}
      proOptions={{ hideAttribution: true }}
    >
      <Background color="#e5e7eb" gap={20} size={1} />
      <Controls />
      <MiniMap nodeColor="#d1d5db" maskColor="rgba(243,244,246,0.6)" />
    </ReactFlow>
  )
}
